import { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical, Eye, EyeOff, Plus } from "lucide-react";
import {
  DOCUMENT_BLOCK_LABELS,
  DOCUMENT_TEMPLATE_LABELS,
  availableDocumentTypes,
  createDocumentBlock,
  type DocumentBlock,
  type DocumentBlockKind,
  type DocumentTemplateType,
} from "@gaole/shared";
import { api } from "../lib/api";
import { toast } from "../stores/toast";
import { useAuth } from "../stores/auth";
import { useServerDraft } from "../hooks/useServerDraft";
import { showQueryLoader } from "../lib/query-utils";
import { Button, Input, PageLoader, PillSegmentSwitcher, SettingsCard, Toggle } from "../components/ui";

const BLOCK_KINDS = Object.keys(DOCUMENT_BLOCK_LABELS) as DocumentBlockKind[];

interface TemplateRow {
  type: DocumentTemplateType;
  blocks: DocumentBlock[];
  updatedAt: string;
}

function SortableBlockRow({
  block,
  onToggle,
  onUpdate,
}: {
  block: DocumentBlock;
  onToggle: () => void;
  onUpdate: (next: DocumentBlock) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: block.id,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="rounded-xl border border-line bg-white/[0.02] p-3"
    >
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab text-white/40 hover:text-white/70"
          {...attributes}
          {...listeners}
          aria-label="Drag to reorder"
        >
          <GripVertical size={16} />
        </button>
        <span className="flex-1 text-sm font-medium">{DOCUMENT_BLOCK_LABELS[block.kind]}</span>
        <button
          type="button"
          className="text-white/50 hover:text-white"
          onClick={onToggle}
          aria-label={block.enabled === false ? "Show block" : "Hide block"}
        >
          {block.enabled === false ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
      </div>

      {block.enabled !== false && block.kind === "document_title" && (
        <Input
          className="mt-2"
          label="Title"
          value={block.title ?? ""}
          onChange={(e) => onUpdate({ ...block, title: e.target.value })}
        />
      )}
      {block.enabled !== false && block.kind === "customer_info" && (
        <div className="mt-2">
          <Toggle
            label="Show shipping address"
            checked={block.showShipping !== false}
            onChange={(v) => onUpdate({ ...block, showShipping: v })}
          />
        </div>
      )}
      {block.enabled !== false && block.kind === "totals" && (
        <div className="mt-2 space-y-2">
          <Toggle
            label="Show tax row"
            checked={block.showTax !== false}
            onChange={(v) => onUpdate({ ...block, showTax: v })}
          />
          <Toggle
            label="Show shipping row"
            checked={block.showShipping !== false}
            onChange={(v) => onUpdate({ ...block, showShipping: v })}
          />
          <Toggle
            label="Show discount row"
            checked={block.showDiscount !== false}
            onChange={(v) => onUpdate({ ...block, showDiscount: v })}
          />
        </div>
      )}
      {block.enabled !== false && block.kind === "footer_notes" && (
        <Input
          className="mt-2"
          label="Footer text"
          value={block.text ?? ""}
          onChange={(e) => onUpdate({ ...block, text: e.target.value })}
          hint="Leave empty to use company default footer from settings."
        />
      )}
    </div>
  );
}

export function DocumentTemplates() {
  const queryClient = useQueryClient();
  const { moduleEnabled } = useAuth();
  const templateTypes = useMemo(
    () =>
      availableDocumentTypes({
        documents: moduleEnabled("documents"),
        invoicing: moduleEnabled("invoicing"),
      }),
    [moduleEnabled],
  );
  const [activeType, setActiveType] = useState<DocumentTemplateType>("RECEIPT");
  const [previewHtml, setPreviewHtml] = useState("");

  const { data, isLoading } = useQuery({
    queryKey: ["document-templates"],
    queryFn: () => api<{ items: TemplateRow[] }>("/documents/templates").then((r) => r.items),
  });

  const activeTemplate = useMemo(
    () => data?.find((t) => t.type === activeType),
    [data, activeType],
  );

  const {
    value: blocks,
    setValue: setBlocks,
    reset: resetBlocks,
    isDirty: dirty,
  } = useServerDraft(activeTemplate?.blocks, activeType);

  useEffect(() => {
    if (templateTypes.length && !templateTypes.includes(activeType)) {
      setActiveType(templateTypes[0]!);
    }
  }, [activeType, templateTypes]);

  useEffect(() => {
    if (!blocks?.length) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const html = await api<{ html: string }>(`/documents/templates/${activeType}/preview`, {
            method: "POST",
            body: { blocks },
          }).then((r) => r.html);
          if (!cancelled) setPreviewHtml(html);
        } catch (e) {
          if (!cancelled) toast("error", e instanceof Error ? e.message : "Preview failed");
        }
      })();
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [blocks, activeType]);

  const saveMut = useMutation({
    mutationFn: () =>
      api(`/documents/templates/${activeType}`, {
        method: "PUT",
        body: { blocks },
      }),
    onSuccess: () => {
      toast("success", "Template saved");
      resetBlocks();
      void queryClient.invalidateQueries({ queryKey: ["document-templates"] });
    },
    onError: (e: Error) => toast("error", e.message),
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const onDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setBlocks((items) => {
      const oldIndex = items.findIndex((b) => b.id === active.id);
      const newIndex = items.findIndex((b) => b.id === over.id);
      return arrayMove(items, oldIndex, newIndex);
    });
  }, [setBlocks]);

  function updateBlock(id: string, next: DocumentBlock) {
    setBlocks((items) => items.map((b) => (b.id === id ? next : b)));
  }

  function toggleBlock(id: string) {
    setBlocks((items) =>
      items.map((b) => (b.id === id ? { ...b, enabled: b.enabled === false ? true : false } : b)),
    );
  }

  function addBlock(kind: DocumentBlockKind) {
    setBlocks((items) => [...items, createDocumentBlock(kind)]);
  }

  function resetDraft() {
    resetBlocks();
  }

  async function refreshPreview() {
    try {
      const html = await api<{ html: string }>(`/documents/templates/${activeType}/preview`, {
        method: "POST",
        body: { blocks },
      }).then((r) => r.html);
      setPreviewHtml(html);
    } catch (e) {
      toast("error", e instanceof Error ? e.message : "Preview failed");
    }
  }

  if (showQueryLoader({ isLoading, data })) return <PageLoader />;

  const resolvedBlocks = blocks ?? [];
  const usedKinds = new Set(resolvedBlocks.map((b) => b.kind));
  const addableKinds = BLOCK_KINDS.filter((k) => !usedKinds.has(k) || k === "footer_notes");

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-lg font-bold">Document templates</h1>
          <p className="mt-0.5 text-xs text-white/50">
            Customize payment receipt and tax invoice layouts. Drag blocks to reorder.
          </p>
        </div>
        {templateTypes.length > 1 ? (
          <PillSegmentSwitcher
            value={activeType}
            onChange={(v) => setActiveType(v)}
            options={templateTypes.map((t) => ({ value: t, label: DOCUMENT_TEMPLATE_LABELS[t] }))}
          />
        ) : (
          <span className="text-xs text-white/50">{DOCUMENT_TEMPLATE_LABELS[activeType]}</span>
        )}
      </div>

      <div className="grid gap-5 lg:grid-cols-2">
        <SettingsCard title="Blocks" description="Toggle visibility and adjust block options.">
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
            <SortableContext items={resolvedBlocks.map((b) => b.id)} strategy={verticalListSortingStrategy}>
              <div className="space-y-2">
                {resolvedBlocks.map((block) => (
                  <SortableBlockRow
                    key={block.id}
                    block={block}
                    onToggle={() => toggleBlock(block.id)}
                    onUpdate={(next) => updateBlock(block.id, next)}
                  />
                ))}
              </div>
            </SortableContext>
          </DndContext>

          {addableKinds.length > 0 && (
            <div className="mt-4 flex flex-wrap gap-2">
              {addableKinds.map((kind) => (
                <Button key={kind} size="sm" variant="outline" onClick={() => addBlock(kind)}>
                  <Plus size={14} className="mr-1" /> {DOCUMENT_BLOCK_LABELS[kind]}
                </Button>
              ))}
            </div>
          )}
        </SettingsCard>

        <SettingsCard title="Live preview" description="Sample order data — updates as you edit.">
          {!previewHtml ? (
            <PageLoader />
          ) : (
            <iframe
              title="Document preview"
              className="h-[640px] w-full rounded-lg border border-line bg-white"
              srcDoc={previewHtml}
            />
          )}
        </SettingsCard>
      </div>

      <div className="sticky bottom-0 flex flex-wrap justify-end gap-2 rounded-xl border border-line bg-surface/95 p-3 backdrop-blur">
        <Button size="sm" variant="outline" disabled={!dirty} onClick={resetDraft}>
          Cancel
        </Button>
        <Button size="sm" variant="outline" onClick={() => void refreshPreview()}>
          Refresh preview
        </Button>
        <Button size="sm" disabled={saveMut.isPending || !dirty} onClick={() => saveMut.mutate()}>
          Save
        </Button>
      </div>
    </div>
  );
}
